// useStewNudges — fetches the current user's active Stew nudges +
// exposes dismiss / act handlers.
//
// CONSUMER: web (TopNav badge + StewChatPanel banner).
//
// Single hook used by both surfaces so they share one in-flight
// request + one optimistic-update path. Module-scoped state means
// the badge in the nav + the banner in the open panel show the
// same list at the same time without prop drilling.
//
// Polling: every 5 minutes via setInterval. Cheap (single indexed
// query, ≤10 rows). The nudges are daily-generated so 5-min poll
// catches the cron's morning write within the first quarter-hour
// of a workday.

import { useCallback, useEffect, useState } from "react";

import { api } from "../api";

export interface StewNudge {
  id: string;
  kind: string;
  generated_at: string;
  title: string;
  sub: string | null;
  rationale: string | null;
  related_entity_type: string | null;
  related_entity_id: string | null;
  href: string | null;
}

interface NudgeStore {
  items: StewNudge[];
  loadedAt: number; // epoch ms; 0 = not yet loaded
}

// Module-scoped store + listener fanout. React doesn't have a built-in
// "shared hook state" primitive so we roll a tiny pub/sub.
let store: NudgeStore = { items: [], loadedAt: 0 };
const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

const POLL_INTERVAL_MS = 5 * 60 * 1000;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let activeConsumers = 0;

async function refresh() {
  try {
    const res = await api.listStewNudges();
    store = {
      items: res.items.slice(),
      loadedAt: Date.now(),
    };
    notify();
  } catch {
    // Silent — keep prior state. The badge stays at its last-known
    // count rather than flapping to 0 on a transient error.
  }
}

function ensurePolling() {
  if (pollTimer !== null) return;
  pollTimer = setInterval(() => {
    void refresh();
  }, POLL_INTERVAL_MS);
}

function tearDownPolling() {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/**
 * Reset the module-scoped nudge cache. MUST be called on every
 * effective-user transition (sign-in, sign-out, start/stop
 * impersonation) — otherwise the next consumer mount sees
 * `store.loadedAt !== 0` and reuses the PRIOR user's nudges, which
 * name specific households (regulated PII). The store is module-
 * scoped (survives SPA navigation + the SPA-only signOut, which does
 * NOT reload the page), so React unmount alone never clears it.
 *
 * Clears immediately (badge/banner drop to 0 so no stale cross-user
 * data lingers), then — if any consumer is still mounted — refetches
 * for the new effective user so the surfaces repopulate within a
 * round-trip instead of waiting for the 5-min poll.
 */
export function resetStewNudges() {
  store = { items: [], loadedAt: 0 };
  notify();
  if (activeConsumers > 0) {
    void refresh();
  }
}

export interface UseStewNudgesResult {
  items: ReadonlyArray<StewNudge>;
  count: number;
  isLoaded: boolean;
  /** Dismiss a nudge. Optimistic — removes from local state immediately. */
  dismiss: (id: string) => Promise<void>;
  /** Signal a click-through (audit only). Fire-and-forget. */
  act: (id: string) => void;
  /** Force-refetch. Useful after the user takes an action elsewhere. */
  refetch: () => Promise<void>;
}

export function useStewNudges(): UseStewNudgesResult {
  const [, force] = useState({});

  useEffect(() => {
    activeConsumers++;
    // V1.5.1 audit fix — listener fanout was broken: the prior
    // cleanup called `listeners.clear()`, which wiped ALL listeners
    // (not just this consumer's). With TopNav badge + StewChatPanel
    // both mounted, unmounting either one stopped notify() from
    // reaching the other — the badge or banner would silently stop
    // updating. Per-effect listener function + listeners.delete(fn)
    // on cleanup is the standard pub/sub pattern.
    const listener = () => force({});
    listeners.add(listener);
    // First mount of any consumer triggers a fetch + starts polling.
    if (store.loadedAt === 0) {
      void refresh();
    }
    ensurePolling();
    return () => {
      activeConsumers--;
      listeners.delete(listener);
      if (activeConsumers <= 0) {
        tearDownPolling();
      }
    };
  }, []);

  const dismiss = useCallback(async (id: string) => {
    // Optimistic — drop from local store first; revert on error.
    const prev = store.items;
    store = {
      items: store.items.filter((n) => n.id !== id),
      loadedAt: store.loadedAt,
    };
    notify();
    try {
      await api.dismissStewNudge(id);
    } catch {
      // Revert.
      store = { items: prev, loadedAt: store.loadedAt };
      notify();
    }
  }, []);

  const act = useCallback((id: string) => {
    // Fire-and-forget — audit only. Don't optimistically drop the
    // row (acting doesn't mean the user dismissed it).
    void api.actOnStewNudge(id).catch(() => {
      /* silent */
    });
  }, []);

  const refetch = useCallback(async () => {
    await refresh();
  }, []);

  return {
    items: store.items,
    count: store.items.length,
    isLoaded: store.loadedAt > 0,
    dismiss,
    act,
    refetch,
  };
}
